import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { useBoards } from "features/analytics/studio/hooks/useBoards";
import Icon from "shared/components/Icon";

// Custom Icon components using Icon component
const InsightsIcon = ({ color = "currentColor" }) => (
  <Icon name="analytics" size={18} color={color} />
);

const FunnelsIcon = ({ color = "currentColor" }) => (
  <Icon name="funnel" size={18} color={color} />
);

const FlowsIcon = ({ color = "currentColor" }) => (
  <Icon name="flows" size={18} color={color} />
);

const RetentionIcon = ({ color = "currentColor" }) => (
  <Icon name="retention" size={18} color={color} />
);

const BoardIcon = ({ color = "currentColor" }) => (
  <Icon name="dashboard" size={18} color={color} />
);

// REPORT section – creation menu order
const REPORT_ITEMS = [
  { id: "insights", label: "Insights", icon: InsightsIcon },
  { id: "funnels", label: "Funnels", icon: FunnelsIcon },
  { id: "flows", label: "Flows", icon: FlowsIcon },
  { id: "retention", label: "Retention", icon: RetentionIcon },
];

const ReportsIcon = ({ color = "currentColor" }) => (
  <Icon name="analytics" size={18} color={color} />
);

// ANALYSIS section – Reports, Board (heatmap route disabled in app)
const ANALYSIS_ITEMS = [
  { id: "reports", label: "Reports", icon: ReportsIcon },
  { id: "board", label: "Board", icon: BoardIcon },
];

export default function CreateNewDropdown({ open, onClose, anchorRef }) {
  const [isOpen, setIsOpen] = useState(!!open);
  const [menuPos, setMenuPos] = useState(null);
  const navigate = useNavigate();
  const { team } = useParams();
  /** Always "new" for report types from this menu — do not inherit :boardId from the current route (e.g. last viewed board). Board-scoped creation uses AddToBoardDropdown or explicit navigates with a real id. */
  const bid = "new";
  const dropdownRef = useRef(null);
  const { createBoard } = useBoards();

  useEffect(() => {
    setIsOpen(!!open);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const compute = () => {
      const a = anchorRef?.current;
      if (!a) return;
      const rect = a.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(280, Math.max(240, window.innerWidth - 2 * margin));
      let left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
      const top = rect.bottom + margin;
      setMenuPos({ top, left, width });
    };
    compute();
    const id = requestAnimationFrame(compute);
    window.addEventListener("resize", compute);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", compute);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose, anchorRef]);

  const handleSelect = async (id) => {
    onClose?.();

    if (id === "reports") {
      navigate(`/${team}/analytics/reports`);
      return;
    }

    if (id === "board") {
      // Create board and open it directly
      try {
        const newBoard = await createBoard({ name: "Untitled", description: null });
        if (newBoard?.id) {
          navigate(`/${team}/analytics/boards/${newBoard.id}`);
        }
      } catch (err) {
        console.error("Failed to create board:", err);
      }
      return;
    }

    // Flows uses its own dedicated editor page
    if (id === "flows") {
      navigate(`/${team}/analytics/flow/${bid}`);
      return;
    }

    // Report types → open editor with type
    if (["insights", "funnels", "retention"].includes(id)) {
      navigate(`/${team}/analytics/boards/${bid}/editor?type=${id}`);
    }
  };

  if (!isOpen || !menuPos || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="rounded-lg border shadow-xl py-1 min-w-[240px]"
      style={{
        position: "fixed",
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        zIndex: 9999,
        pointerEvents: "auto",
        fontFamily: "Inter, sans-serif",
        backgroundColor: theme.bgRaised,
        borderColor: theme.border,
      }}
    >
      {/* REPORT */}
      <div
        className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: theme.textMuted }}
      >
        Report
      </div>
      {REPORT_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item.id)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.primaryLight}15`;
              e.currentTarget.style.color = theme.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = theme.textSecondary;
            }}
          >
            <span className="flex-shrink-0">
              <Icon />
            </span>
            {item.label}
          </button>
        );
      })}

      <div className="border-t my-1" style={{ borderColor: theme.border }} />

      {/* ANALYSIS */}
      <div
        className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: theme.textMuted }}
      >
        Analysis
      </div>
      {ANALYSIS_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item.id)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.primaryLight}15`;
              e.currentTarget.style.color = theme.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = theme.textSecondary;
            }}
          >
            <span className="flex-shrink-0">
              <Icon />
            </span>
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{ backgroundColor: theme.bgOverlay, color: theme.textMuted }}
              >
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>,
    document.body
  );
}
